import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X, Image as ImageIcon, Loader } from 'lucide-react';
import { getUserArtworkAcrossCircles } from '../services/galleryService';

export default function MyGalleryModal({ isOpen, onClose, onSelectImage, currentUserId }) {
  const [artwork, setArtwork] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);

  useEffect(() => {
    if (isOpen && currentUserId) {
      loadUserArtwork();
    }
  }, [isOpen, currentUserId]);

  const loadUserArtwork = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const userArtwork = await getUserArtworkAcrossCircles(currentUserId);
      setArtwork(userArtwork);
      if (userArtwork.length === 0) {
        setError('No artwork found. Create some art first!');
      }
    } catch (err) {
      console.error('Failed to load artwork:', err);
      setError('Failed to load your gallery. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageClick = (artwork) => {
    setSelectedImageUrl(artwork.fullResImageUrl || artwork.imageUrl);
  };

  const handleConfirmSelection = () => {
    if (selectedImageUrl) {
      onSelectImage(selectedImageUrl);
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleBackdropClick}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>
            <ImageIcon size={20} />
            My Gallery
          </Title>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </Header>

        {isLoading && (
          <LoadingState>
            <Loader className="spinner" size={32} />
            <p>Loading your artwork...</p>
          </LoadingState>
        )}

        {error && (
          <ErrorState>
            <p>{error}</p>
            <RetryButton onClick={loadUserArtwork}>Retry</RetryButton>
          </ErrorState>
        )}

        {!isLoading && !error && artwork.length > 0 && (
          <>
            <Subtitle>
              Select an image from your past creations across all circles
            </Subtitle>
            <GalleryGrid>
              {artwork.map((art) => (
                <ArtworkCard
                  key={art.id}
                  onClick={() => handleImageClick(art)}
                  $isSelected={selectedImageUrl === (art.fullResImageUrl || art.imageUrl)}
                >
                  <ArtworkImage src={art.imageUrl} alt="Your artwork" loading="lazy" />
                  <ArtworkOverlay $isSelected={selectedImageUrl === (art.fullResImageUrl || art.imageUrl)}>
                    {selectedImageUrl === (art.fullResImageUrl || art.imageUrl) && (
                      <SelectionIndicator>✓</SelectionIndicator>
                    )}
                  </ArtworkOverlay>
                  <CircleLabel>{art.circleName}</CircleLabel>
                </ArtworkCard>
              ))}
            </GalleryGrid>
            
            <Footer>
              <CancelButton onClick={onClose}>Cancel</CancelButton>
              <ConfirmButton 
                onClick={handleConfirmSelection} 
                disabled={!selectedImageUrl}
              >
                Use Selected Image
              </ConfirmButton>
            </Footer>
          </>
        )}
      </ModalContainer>
    </Overlay>
  );
}

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 800px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  
  @media (min-width: 500px) {
    padding: 20px 24px;
  }
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  color: #111827;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #111827;
  }
`;

const Subtitle = styled.p`
  font-size: 0.9375rem;
  color: #6b7280;
  margin: 0;
  padding: 12px 16px 4px;
  
  @media (min-width: 500px) {
    padding: 12px 20px 4px;
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  gap: 10px;
  padding: 12px 12px 24px;
  overflow-y: auto;
  flex: 1;
  align-content: start;
  
  /* Mobile: 3 columns to match Circle Gallery */
  grid-template-columns: repeat(3, 1fr);
  
  /* Tablet and above: 4 columns */
  @media (min-width: 600px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    padding: 16px 16px 24px;
  }
  
  /* Desktop: 5 columns */
  @media (min-width: 900px) {
    grid-template-columns: repeat(5, 1fr);
    gap: 14px;
    padding: 20px 20px 28px;
  }
`;

const ArtworkCard = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 100%;
  overflow: hidden;
  border-radius: 10px;
  cursor: pointer;
  background: #f9fafb;
  border: ${props => props.$isSelected ? '3px solid #f59e0b' : 'none'};
  transition: all 0.2s;
  box-shadow: ${props => props.$isSelected ? '0 4px 16px rgba(245, 158, 11, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)'};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.$isSelected ? '0 6px 20px rgba(245, 158, 11, 0.4)' : '0 4px 16px rgba(0, 0, 0, 0.12)'};
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const ArtworkImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ArtworkOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.$isSelected ? 'rgba(245, 158, 11, 0.2)' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
`;

const SelectionIndicator = styled.div`
  width: 32px;
  height: 32px;
  background: #f59e0b;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const CircleLabel = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.4) 70%, transparent 100%);
  color: white;
  font-size: 12px;
  padding: 24px 6px 8px;
  text-align: center;
  font-weight: 600;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #6b7280;
  
  .spinner {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  p {
    margin-top: 1rem;
  }
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #ef4444;
  
  p {
    margin-bottom: 1rem;
  }
`;

const RetryButton = styled.button`
  background: #f59e0b;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #d97706;
  }
`;

const Footer = styled.div`
  display: flex;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
  
  @media (min-width: 500px) {
    padding: 20px 24px;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  background: #f3f4f6;
  color: #374151;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const ConfirmButton = styled.button`
  flex: 2;
  background: #f59e0b;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  
  &:hover:not(:disabled) {
    background: #d97706;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(245, 158, 11, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
